import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../lib/api';
import { showToast } from '../components/Toast';
import type { DashboardStats, Alert } from '../lib/types';

export function Dashboard({ onNavigate }: { onNavigate?: (page: string) => void }) {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const touchedRef = useRef(false);

  useEffect(() => {
    Promise.all([
      api.getDashboardStats(),
      api.getAlerts({ status: 'open' }),
    ]).then(([statsRes, alertsRes]) => {
      setStats(statsRes.stats);
      setAlerts(alertsRes.alerts.slice(0, 10));
    }).catch(() => showToast('Failed to load dashboard')).finally(() => setLoading(false));
  }, []);

  // Touch-safe navigation: use onTouchEnd on touch devices, onClick on desktop
  // Prevents double-fire by tracking whether touch already handled it
  const nav = useCallback((page: string) => {
    onNavigate?.(page);
  }, [onNavigate]);

  const touchEnd = useCallback((page: string) => (e: React.TouchEvent) => {
    e.preventDefault();
    touchedRef.current = true;
    nav(page);
    setTimeout(() => { touchedRef.current = false; }, 300);
  }, [nav]);

  const click = useCallback((page: string) => () => {
    if (!touchedRef.current) nav(page);
  }, [nav]);

  if (loading) return (
    <div className="page-content">
      <h1 className="page-title">Dashboard</h1>
      <div className="kpi-grid">
        {[1,2,3,4,5].map(i => <div key={i} className="kpi-card skeleton skeleton-card" />)}
      </div>
    </div>
  );

  return (
    <div className="page-content">
      <h1 className="page-title">Dashboard</h1>

      {/* KPI Cards — clickable, navigate to relevant page */}
      <div className="kpi-grid">
        <button className="kpi-card kpi-clickable" onTouchEnd={touchEnd('buses')} onClick={click('buses')} type="button">
          <div className="kpi-value">{stats?.active_buses || 0}</div>
          <div className="kpi-label">Active Buses</div>
          <div className="kpi-sub">of {stats?.total_buses || 0} total</div>
        </button>
        <button className="kpi-card kpi-clickable" onTouchEnd={touchEnd('trips')} onClick={click('trips')} type="button">
          <div className="kpi-value">{stats?.active_trips || 0}</div>
          <div className="kpi-label">Active Trips</div>
        </button>
        <button className="kpi-card kpi-clickable" onTouchEnd={touchEnd('live')} onClick={click('live')} type="button">
          <div className="kpi-value">{stats?.checkins_today || 0}</div>
          <div className="kpi-label">Check-ins Today</div>
        </button>
        <button className="kpi-card kpi-clickable" onTouchEnd={touchEnd('students')} onClick={click('students')} type="button">
          <div className="kpi-value">{stats?.total_students || 0}</div>
          <div className="kpi-label">Students</div>
        </button>
        <button className="kpi-card kpi-alert kpi-clickable" onTouchEnd={touchEnd('safety')} onClick={click('safety')} type="button">
          <div className="kpi-value">{stats?.alerts_today || 0}</div>
          <div className="kpi-label">Open Alerts</div>
        </button>
      </div>

      {/* Recent Alerts */}
      {alerts.length > 0 && (
        <div className="dashboard-section">
          <h2>Recent Alerts</h2>
          <div className="alert-list">
            {alerts.map(alert => (
              <div key={alert.id} className={`alert-item alert-${alert.severity}`}>
                <div className="alert-header">
                  <span className={`alert-badge badge-${alert.severity}`}>{alert.severity}</span>
                  <span className="alert-type">{alert.type}</span>
                  <span className="alert-time">{new Date(alert.created_at).toLocaleString()}</span>
                </div>
                <div className="alert-title">{alert.title}</div>
                {alert.message && <div className="alert-message">{alert.message}</div>}
                {!alert.resolved && (
                  <button className="btn-sm btn-outline" onClick={async () => {
                    await api.resolveAlert(alert.id);
                    setAlerts(prev => prev.filter(a => a.id !== alert.id));
                  }}>Resolve</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
